import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
    Building2, Users, Truck, Search, MoreVertical, 
    Edit, Trash2, Eye, Power, PowerOff, Calendar,
    Loader2, Shield, ArrowLeft, UserCheck, Receipt
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function CompanyManagement() {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterPlan, setFilterPlan] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    
    // Edit modal state
    const [editingCompany, setEditingCompany] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);
    
    // Detail modal state
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [companyDetail, setCompanyDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        try {
            const response = await api.get('/super-admin/companies');
            setCompanies(response.data);
        } catch (error) {
            console.error('Failed to fetch companies:', error);
            toast.error('Failed to load companies');
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanyDetail = async (companyId) => {
        setLoadingDetail(true);
        try {
            const response = await api.get(`/super-admin/companies/${companyId}`);
            setCompanyDetail(response.data);
        } catch (error) {
            console.error('Failed to fetch company detail:', error);
            toast.error('Failed to load company details');
        } finally {
            setLoadingDetail(false);
        }
    };

    const handleViewCompany = (company) => {
        setSelectedCompany(company);
        fetchCompanyDetail(company.id);
    };

    const handleEditCompany = (company) => {
        setEditingCompany(company);
        setEditForm({
            name: company.name,
            plan: company.plan,
            billing_cycle: company.billing_cycle,
            trial_end: company.trial_end?.split('T')[0] || '',
            is_active: company.is_active
        });
    };

    const handleSaveEdit = async () => {
        setSaving(true);
        try {
            await api.put(`/super-admin/companies/${editingCompany.id}`, {
                ...editForm,
                trial_end: editForm.trial_end ? new Date(editForm.trial_end).toISOString() : undefined
            });
            toast.success('Company updated successfully');
            setEditingCompany(null);
            fetchCompanies();
        } catch (error) {
            console.error('Failed to update company:', error);
            toast.error('Failed to update company');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async (company) => {
        try {
            await api.put(`/super-admin/companies/${company.id}`, {
                is_active: !company.is_active
            });
            toast.success(`Company ${company.is_active ? 'deactivated' : 'activated'}`);
            fetchCompanies();
        } catch (error) {
            console.error('Failed to toggle status:', error);
            toast.error('Failed to update company status');
        }
    };

    const handleDeleteCompany = async (company) => {
        if (!window.confirm(`Are you sure you want to delete "${company.name}"? This will delete ALL data including users, vehicles, and records. This action cannot be undone.`)) {
            return;
        }
        
        try {
            await api.delete(`/super-admin/companies/${company.id}`);
            toast.success('Company deleted successfully');
            fetchCompanies();
        } catch (error) {
            console.error('Failed to delete company:', error);
            toast.error('Failed to delete company');
        }
    };

    const handleImpersonate = async (userId) => {
        try {
            const response = await api.post(`/super-admin/impersonate/${userId}`);
            localStorage.setItem('fleet_token', response.data.access_token);
            localStorage.setItem('fleet_user', JSON.stringify(response.data.user));
            localStorage.setItem('impersonating', 'true');
            toast.success(`Now viewing as ${response.data.user.name}`);
            window.location.href = '/app/dashboard';
        } catch (error) {
            console.error('Failed to impersonate:', error);
            toast.error('Failed to impersonate user');
        }
    };

    const filteredCompanies = companies.filter(company => {
        const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesPlan = filterPlan === 'all' || company.plan === filterPlan;
        const matchesStatus = filterStatus === 'all' || 
            (filterStatus === 'active' && company.is_active) ||
            (filterStatus === 'inactive' && !company.is_active);
        return matchesSearch && matchesPlan && matchesStatus;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="company-management">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => navigate('/app/dashboard')}
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <Shield className="w-5 h-5 text-purple-600" />
                            <span className="text-sm text-purple-600 font-medium">Super Admin</span>
                        </div>
                        <h1 className="text-2xl font-bold text-slate-800">Company Management</h1>
                        <p className="text-slate-500">{companies.length} total companies</p>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                        placeholder="Search companies..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-white border-slate-300"
                    />
                </div>
                <Select value={filterPlan} onValueChange={setFilterPlan}>
                    <SelectTrigger className="w-40 bg-white border-slate-300">
                        <SelectValue placeholder="All Plans" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="all">All Plans</SelectItem>
                                <SelectItem value="basic">Basic</SelectItem>
                                <SelectItem value="professional">Professional</SelectItem>
                                <SelectItem value="enterprise">Enterprise</SelectItem>
                                <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40 bg-white border-slate-300">
                        <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Companies Table */}
            <div className="fleet-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Company</th>
                                <th>Plan</th>
                                <th>Users</th>
                                <th>Vehicles</th>
                                <th>Monthly Billing</th>
                                <th>Trial Ends</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCompanies.map((company) => (
                                <tr key={company.id} className="table-row-hover">
                                    <td>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-[#1B7EB5]/10 rounded-lg flex items-center justify-center">
                                                <Building2 className="w-5 h-5 text-[#1B7EB5]" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-slate-800">{company.name}</p>
                                                <p className="text-xs text-slate-500">
                                                    {new Date(company.created_at).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`px-2 py-1 text-xs rounded-full capitalize ${
                                            company.plan === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                                            company.plan === 'professional' ? 'bg-green-100 text-green-700' :
                                            company.plan === 'basic' ? 'bg-blue-100 text-blue-700' :
                                            'bg-slate-100 text-slate-700'
                                        }`}>
                                            {company.plan}
                                        </span>
                                    </td>
                                    <td className="text-slate-600">{company.user_count}</td>
                                    <td className="text-slate-600">{company.vehicle_count}</td>
                                    <td>
                                        <div className="flex items-center gap-1">
                                            <Receipt className="w-4 h-4 text-[#1B7EB5]" />
                                            <span className="font-medium text-slate-800">
                                                €{company.monthly_charge?.toFixed(2) || '0.00'}
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-500">
                                            €{company.price_per_vehicle || 0}/vehicle
                                        </p>
                                    </td>
                                    <td className="text-slate-600">
                                        {new Date(company.trial_end).toLocaleDateString()}
                                    </td>
                                    <td>
                                        <span className={`px-2 py-1 text-xs rounded-full ${
                                            company.is_active !== false 
                                                ? 'bg-green-100 text-green-700' 
                                                : 'bg-red-100 text-red-700'
                                        }`}>
                                            {company.is_active !== false ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                                    <MoreVertical className="w-4 h-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="bg-white border-slate-200">
                                                <DropdownMenuItem onClick={() => handleViewCompany(company)}>
                                                    <Eye className="w-4 h-4 mr-2" />
                                                    View Details
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => handleEditCompany(company)}>
                                                    <Edit className="w-4 h-4 mr-2" />
                                                    Edit
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => handleToggleStatus(company)}>
                                                    {company.is_active !== false ? (
                                                        <><PowerOff className="w-4 h-4 mr-2" />Deactivate</>
                                                    ) : (
                                                        <><Power className="w-4 h-4 mr-2" />Activate</>
                                                    )}
                                                </DropdownMenuItem>
                                                <DropdownMenuItem 
                                                    onClick={() => handleDeleteCompany(company)}
                                                    className="text-red-600"
                                                >
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                    Delete
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit Company Modal */}
            <Dialog open={!!editingCompany} onOpenChange={() => setEditingCompany(null)}>
                <DialogContent className="bg-white border-slate-200 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800">Edit Company</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <label className="text-sm text-slate-600">Company Name</label>
                            <Input
                                value={editForm.name || ''}
                                onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                                className="bg-white border-slate-300"
                            />
                        </div>
                        <div>
                            <label className="text-sm text-slate-600">Plan</label>
                            <Select value={editForm.plan} onValueChange={(v) => setEditForm({...editForm, plan: v})}>
                                <SelectTrigger className="bg-white border-slate-300">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                    <SelectItem value="personal">Personal</SelectItem>
                                    <SelectItem value="basic">Basic</SelectItem>
                                    <SelectItem value="professional">Professional</SelectItem>
                                    <SelectItem value="enterprise">Enterprise</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-sm text-slate-600">Billing Cycle</label>
                            <Select value={editForm.billing_cycle} onValueChange={(v) => setEditForm({...editForm, billing_cycle: v})}>
                                <SelectTrigger className="bg-white border-slate-300">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                    <SelectItem value="monthly">Monthly</SelectItem>
                                    <SelectItem value="yearly">Yearly</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-sm text-slate-600">Trial End Date</label>
                            <Input
                                type="date"
                                value={editForm.trial_end || ''}
                                onChange={(e) => setEditForm({...editForm, trial_end: e.target.value})}
                                className="bg-white border-slate-300"
                            />
                        </div>
                        <div className="flex gap-3 pt-2">
                            <Button variant="outline" onClick={() => setEditingCompany(null)} className="flex-1">
                                Cancel
                            </Button>
                            <Button onClick={handleSaveEdit} disabled={saving} className="flex-1 bg-[#1B7EB5] text-white">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Company Detail Modal */}
            <Dialog open={!!selectedCompany} onOpenChange={() => setSelectedCompany(null)}>
                <DialogContent className="bg-white border-slate-200 max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-[#1B7EB5]" />
                            {selectedCompany?.name}
                        </DialogTitle>
                    </DialogHeader>
                    {loadingDetail ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 text-[#1B7EB5] animate-spin" />
                        </div>
                    ) : companyDetail && (
                        <div className="space-y-6 mt-4">
                            {/* Company Stats */}
                            <div className="grid grid-cols-3 gap-4">
                                <div className="bg-slate-50 rounded-lg p-3 text-center">
                                    <Users className="w-5 h-5 text-[#1B7EB5] mx-auto mb-1" />
                                    <p className="text-2xl font-bold text-slate-800">{companyDetail.user_count}</p>
                                    <p className="text-xs text-slate-500">Users</p>
                                </div>
                                <div className="bg-slate-50 rounded-lg p-3 text-center">
                                    <Truck className="w-5 h-5 text-[#58A6B4] mx-auto mb-1" />
                                    <p className="text-2xl font-bold text-slate-800">{companyDetail.vehicle_count}</p>
                                    <p className="text-xs text-slate-500">Vehicles</p>
                                </div>
                                <div className="bg-slate-50 rounded-lg p-3 text-center">
                                    <UserCheck className="w-5 h-5 text-[#8CC774] mx-auto mb-1" />
                                    <p className="text-2xl font-bold text-slate-800">{companyDetail.driver_count}</p>
                                    <p className="text-xs text-slate-500">Drivers</p>
                                </div>
                            </div>

                            {/* Company Info */}
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="text-slate-500">Plan:</span>
                                    <span className="ml-2 text-slate-800 capitalize">{companyDetail.plan}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500">Billing:</span>
                                    <span className="ml-2 text-slate-800 capitalize">{companyDetail.billing_cycle}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500">Trial Start:</span>
                                    <span className="ml-2 text-slate-800">{new Date(companyDetail.trial_start).toLocaleDateString()}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500">Trial End:</span>
                                    <span className="ml-2 text-slate-800">{new Date(companyDetail.trial_end).toLocaleDateString()}</span>
                                </div>
                            </div>

                            {/* Users List */}
                            <div>
                                <h4 className="text-sm font-semibold text-slate-700 mb-3">Users ({companyDetail.users?.length || 0})</h4>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {companyDetail.users?.map((user) => (
                                        <div key={user.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-[#1B7EB5]/10 rounded-full flex items-center justify-center text-[#1B7EB5] text-sm font-medium">
                                                    {user.name.charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-slate-800">{user.name}</p>
                                                    <p className="text-xs text-slate-500">{user.email}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className={`text-xs px-2 py-1 rounded capitalize ${
                                                    user.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                                                    user.role === 'fleet_manager' ? 'bg-blue-100 text-blue-700' :
                                                    'bg-slate-100 text-slate-700'
                                                }`}>
                                                    {user.role.replace('_', ' ')}
                                                </span>
                                                <Button 
                                                    size="sm" 
                                                    variant="ghost"
                                                    onClick={() => handleImpersonate(user.id)}
                                                    className="h-7 text-xs"
                                                >
                                                    Login As
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
